import React, { useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw, Download, Check, ChevronDown, ChevronUp } from 'lucide-react';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import AddImportTemplate from '../../pages/Enquiries/Forms/AddImportTemplate';
import { getSession } from '../../getSession';
import { addLeadImportSummary, bindUpdateFields, getLeadTemplateJson, getMappingFieldColumns, updateMassLeadEnquiryFromExcel, getMappingByDropdownSelection, getPredefinedMappings } from '../../utils/lead';

const ImportData = ({ isOpen, onClose, registerSubmitHandler, onSubmit }) => {
  const { userId, parentId, TokenId } = getSession();
  const [importMode, setImportMode] = useState('bulk-import');
  const [selectedTemplate, setSelectedTemplate] = useState('0');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [updateMatching, setUpdateMatching] = useState(false);
  const [isFieldDropdownOpen, setIsFieldDropdownOpen] = useState(false);
  const [fieldSearchTerm, setFieldSearchTerm] = useState('');
  const [isAddTemplateModalOpen, setIsAddTemplateModalOpen] = useState(false);
  const [isInstructionCollapsed, setIsInstructionCollapsed] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isLoadingBulkUpdateFields, setIsLoadingBulkUpdateFields] = useState(false);
  const [templateColumns, setTemplateColumns] = useState([]);
  const [bulkUpdateApiFields, setBulkUpdateApiFields] = useState([]);
  const [templates, setTemplates] = useState([
    { value: '0', label: 'PredefinedField' },
    { value: '1', label: 'NewOne' }
  ]);

  const bulkUpdateFields = useMemo(
    () => bulkUpdateApiFields.map((field) => ({ value: field.label, label: field.label })),
    [bulkUpdateApiFields]
  );

  const filteredFields = bulkUpdateFields.filter((field) =>
    field.label.toLowerCase().includes(fieldSearchTerm.toLowerCase())
  );

  const parseMaybeJson = (value) => {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const normalizeApiDetails = (payload) => {
    const body = payload?.data ?? payload;
    const details = body?.Details ?? body?.data ?? body?.d ?? '';
    return parseMaybeJson(details);
  };

  const normalizeTemplateColumns = (payload) => {
    const details = normalizeApiDetails(payload);
    const rows = Array.isArray(details)
      ? details
      : Array.isArray(details?.data)
        ? details.data
        : [];

    return rows
      .map((item) => String(
        item?.COLUMN_NAME
        ?? item?.ColumnName
        ?? item?.column_name
        ?? item?.FieldName
        ?? item?.fieldName
        ?? item?.label
        ?? item?.value
        ?? ''
      ).trim())
      .filter(Boolean);
  };

  const buildApiPayload = (details = {}) => ({
    Token: Number(TokenId),
    Message: '',
    LoggedUserId: userId,
    MAC_Address: '',
    IP_Address: '',
    Details: details,
    BroadcastName: ''
  });

  const loadLeadTemplateFields = async () => {
    try {
      setIsLoadingTemplate(true);
      const payload = buildApiPayload({
        ParentId: parentId ? Number(parentId) : 0
      });
      const response = await getLeadTemplateJson(payload);

      if (response?.Status !== 1) {
        throw new Error(response?.Message || 'Unable to load lead import template.');
      }

      const columns = normalizeTemplateColumns(response);
      setTemplateColumns(columns);
    } catch (error) {
      console.error('GetLeadTemplateJson error:', error);
      setTemplateColumns([]);
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const loadPredefinedMappings = async () => {
    try {
      const payload = buildApiPayload({
        ParentId: parentId ? Number(parentId) : 0
      });
      const response = await getPredefinedMappings(payload);
      if (response?.Status === 1) {
        const details = normalizeApiDetails(response);
        const rows = Array.isArray(details)
          ? details
          : Array.isArray(details?.data)
            ? details.data
            : [];
        const mappedTemplates = rows.map(r => ({
          value: String(r.MappingId ?? r.mappingId ?? r.LField_MappingId ?? ''),
          label: String(r.MappingName ?? r.mappingName ?? '')
        }));
        setTemplates([
          { value: '0', label: 'PredefinedField' },
          ...mappedTemplates
        ]);
      }
    } catch (error) {
      console.error('loadPredefinedMappings error:', error);
    }
  };

  useEffect(() => {
    loadLeadTemplateFields();
    loadPredefinedMappings();
  }, []);

  const loadBulkUpdateFields = async () => {
    try {
      setIsLoadingBulkUpdateFields(true);
      const mapped = await bindUpdateFields({
        tableName: 'UserLeads',
        scheduledatetimeforlead: '',
        UserId: Number(userId || 0)
      });
      setBulkUpdateApiFields(Array.isArray(mapped) ? mapped : []);
    } catch (error) {
      console.error('BindUpdateField load error:', error);
      setBulkUpdateApiFields([]);
    } finally {
      setIsLoadingBulkUpdateFields(false);
    }
  };

  useEffect(() => {
    if (importMode === 'bulk-update' && bulkUpdateApiFields.length === 0 && !isLoadingBulkUpdateFields) {
      loadBulkUpdateFields();
    }
  }, [importMode, bulkUpdateApiFields.length, isLoadingBulkUpdateFields]);

  useEffect(() => {
    if (typeof registerSubmitHandler !== 'function') return undefined;
    registerSubmitHandler(() => handleUpload);
    return () => registerSubmitHandler(null);
  }, [registerSubmitHandler, selectedFile, selectedTemplate, selectedFields, importMode, templateColumns, userId, parentId]);

  const handleFieldToggle = (fieldValue) => {
    setSelectedFields((prev) =>
      prev.includes(fieldValue)
        ? prev.filter((f) => f !== fieldValue)
        : (() => {
          if (prev.length >= 5) {
            Swal.fire('Warning', 'Please select atleast one field and max 5', 'warning');
            return prev;
          }
          return [...prev, fieldValue];
        })()
    );
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const getMappingDetails = async (mappingId) => {
    const response = await getMappingFieldColumns(
      buildApiPayload({ MappingId: Number(mappingId || 0) })
    );

    if (response?.Status !== 1) {
      throw new Error(response?.Message || 'Unable to load mapping field columns.');
    }

    const details = normalizeApiDetails(response);
    const parsed = parseMaybeJson(details);
    return parsed && typeof parsed === 'object' ? parsed : {};
  };

  const readExcelRows = async (file) => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const firstSheet = workbook.SheetNames[0];
    if (!firstSheet) return [];
    const worksheet = workbook.Sheets[firstSheet];
    return XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false });
  };

  const normalizeImportedRows = (rows, mappingDict = null, requiredCols = null) => {
    if (!Array.isArray(rows)) return [];

    let normalizedRows = rows.map((record) => {
      const baseRecord = {};
      Object.keys(record || {}).forEach((key) => {
        baseRecord[String(key || '').trim()] = record[key];
      });
      return baseRecord;
    });

    if (mappingDict && Object.keys(mappingDict).length > 0) {
      normalizedRows = normalizedRows.map((record) => {
        const filteredRecord = {};

        Object.keys(record).forEach((key) => {
          const originalKey = Object.keys(mappingDict).find((mappingKey) => String(mappingDict[mappingKey] || '').trim() === key);
          if (originalKey) {
            filteredRecord[originalKey] = record[key];
          } else {
            filteredRecord[key] = record[key];
          }
        });

        Object.entries(mappingDict)
          .filter(([, value]) => String(value || '').includes('Other Field'))
          .forEach(([key, value]) => {
            filteredRecord[key] = String(value || '').replace(' (Other Field)', '');
          });

        return filteredRecord;
      });
    }

    const columnsToFilter = (requiredCols && requiredCols.length > 0) ? requiredCols : templateColumns;
    if (columnsToFilter.length > 0) {
      normalizedRows = normalizedRows.map((record) => {
        const nextRecord = {};
        columnsToFilter.forEach((column) => {
          nextRecord[column] = Object.prototype.hasOwnProperty.call(record, column)
            ? record[column]
            : null;
        });
        return nextRecord;
      });
    }

    if (importMode === 'bulk-update' && selectedFields.length > 0) {
      normalizedRows = normalizedRows.map((record) => {
        const filteredRecord = {};
        selectedFields.forEach((field) => {
          filteredRecord[field] = Object.prototype.hasOwnProperty.call(record, field)
            ? record[field]
            : null;
        });
        return filteredRecord;
      });
    }

    return normalizedRows;
  };

  const extractImportResultMessage = (details, rowCount) => {
    const parsedDetails = parseMaybeJson(details);
    const resultRows = Array.isArray(parsedDetails)
      ? parsedDetails
      : Array.isArray(parsedDetails?.data)
        ? parsedDetails.data
        : [];

    const firstRow = resultRows[0];
    const importId = firstRow && typeof firstRow === 'object'
      ? Object.values(firstRow).find((value) => value !== null && value !== undefined && String(value).trim() !== '')
      : '';

    return importId
      ? `Import Successful, ImportId:- ${String(importId)} | Row Uploaded:- ${rowCount}`
      : `Import Successful, Row Uploaded:- ${rowCount}`;
  };

  const validateImportFields = async (excelColumns, dropdownSelection) => {
    let result = "";
    if (dropdownSelection === 0) {
      let cols = templateColumns;
      if (!cols || cols.length === 0) {
        try {
          const payload = buildApiPayload({
            ParentId: parentId ? Number(parentId) : 0
          });
          const response = await getLeadTemplateJson(payload);
          cols = normalizeTemplateColumns(response);
        } catch (e) {
          console.error('getLeadTemplateJson error during validation', e);
        }
      }

      if (excelColumns.length === cols.length) {
        excelColumns.forEach((col) => {
          const match = cols.some(c => String(c).trim().toLowerCase() === String(col).trim().toLowerCase());
          if (!match) {
            result += `\nInvalid Upload Field found:- ${col}`;
          }
        });
      } else {
        result = "Upload Field count does not match with Import Field count";
      }
    } else {
      try {
        const respImport = await getMappingByDropdownSelection(buildApiPayload({
          MappingId: Number(dropdownSelection),
          Mode: ""
        }));
        const respOrigImport = await getMappingByDropdownSelection(buildApiPayload({
          MappingId: Number(dropdownSelection),
          Mode: "Original"
        }));

        const parseRows = (payload) => {
          const details = normalizeApiDetails(payload);
          return Array.isArray(details)
            ? details
            : Array.isArray(details?.data)
              ? details.data
              : [];
        };

        const dtImport = parseRows(respImport);
        const dtOrigImport = parseRows(respOrigImport);

        const getColName = (row) => {
          return String(row?.COLUMN_NAME ?? row?.ColumnName ?? row?.column_name ?? '').trim();
        };

        const getOrigColName = (row) => {
          return String(row?.OriginalColumn ?? row?.originalColumn ?? row?.original_column ?? '').trim();
        };

        const allColumnsMatched = (excelColumns.length === dtImport.length);
        let hasErrors = false;

        if (allColumnsMatched) {
          excelColumns.forEach((col) => {
            const check = dtImport.some(row => getColName(row).toLowerCase() === String(col).trim().toLowerCase());
            if (!check) {
              hasErrors = true;
              result += `\nInvalid Upload Field found: ${col} or uploaded Excel does not match the selected import template. Please download the template again, fill it correctly, and try importing.`;
            }
          });
        } else {
          hasErrors = true;
        }

        const fallbackCheck = (excelColumns.length === dtOrigImport.length);
        if (hasErrors && fallbackCheck) {
          let fallbackErrors = false;
          let fallbackResult = "";

          excelColumns.forEach((col) => {
            const check = dtOrigImport.some(row => getOrigColName(row).toLowerCase() === String(col).trim().toLowerCase());
            if (!check) {
              fallbackErrors = true;
              fallbackResult += `\nInvalid Upload Field found: ${col} or uploaded Excel does not match the selected import template. Please download the template again, fill it correctly, and try importing.`;
            }
          });

          if (!fallbackErrors) {
            hasErrors = false;
            result = "";
          } else {
            result = fallbackResult;
          }
        }

        if (hasErrors && result === "") {
          result = "Upload field count does not match the import field count, or the uploaded Excel does not match the selected import template. Please download the template again, fill it correctly, and try importing.";
        }
      } catch (err) {
        console.error("Validation API error:", err);
        result = "Error validating import fields. Please try again or contact support.";
      }
    }

    return result.trim();
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      Swal.fire('Warning', 'Please select XLS or XLSX file.', 'warning');
      return false;
    }

    const fileName = String(selectedFile.name || '');
    const ext = fileName.split('.').pop()?.toLowerCase();

    if (!['xls', 'xlsx'].includes(ext)) {
      Swal.fire('Error', 'Provide only XLS or XLSX file.', 'error');
      return false;
    }

    try {
      setIsUploading(true);
      const rows = await readExcelRows(selectedFile);

      if (!rows.length) {
        Swal.fire('Error', 'No Rows Found to Import', 'error');
        return false;
      }

      if (rows.length > 10000) {
        Swal.fire('Error', 'Maximum 10000 Record allowed in one upload.', 'error');
        return false;
      }

      // Handle Bulk Update flow
      if (importMode === 'bulk-update') {
        if (!selectedFields || selectedFields.length === 0) {
          Swal.fire('Warning', 'Please select at least one field for bulk update.', 'warning');
          return false;
        }

        // Filter rows to only include selected fields
        const updateRows = rows.map((record) => {
          const filteredRecord = {};
          selectedFields.forEach((field) => {
            filteredRecord[field] = Object.prototype.hasOwnProperty.call(record, field)
              ? record[field]
              : null;
          });
          return filteredRecord;
        });

        const updateJson = JSON.stringify(updateRows);
        const response = await updateMassLeadEnquiryFromExcel(
          buildApiPayload({
            userid: Number(userId || 0),
            tablename: 'UserLeads',
            updatevalues: updateJson,
            mode: 'bulk-update',
            TotalCount: Number(updateRows.length || 0),
            filename: String(selectedFile?.name || '')
          })
        );

        if (response?.Status !== 1) {
          throw new Error(response?.Message || 'Error Saving Update Data, Please try again or contact support');
        }

        const successMessage = updateRows.length > 0
          ? `Update Successful | Rows Updated:- ${updateRows.length}`
          : 'Update Successful';
        Swal.fire('Success', successMessage, 'success');

        if (typeof onSubmit === 'function') {
          onSubmit({ file: selectedFile, rows: updateRows, mode: 'bulk-update' });
        }

        if (typeof onClose === 'function') {
          onClose();
        }

        return true;
      }

      // Handle Bulk Import flow (existing logic)
      if (importMode === 'bulk-import') {
        const excelColumns = Object.keys(rows[0] || {}).map(k => String(k).trim());
        const selectedValue = Number(selectedTemplate || 0);
        const validationError = await validateImportFields(excelColumns, selectedValue);
        if (validationError) {
          Swal.fire({
            icon: 'error',
            title: 'Validation Error',
            text: validationError
          });
          return false;
        }
      }

      let mappingDict = null;
      const mappingId = Number(selectedTemplate || 0);
      const normalizedSelectedValue = Number.isFinite(mappingId) ? mappingId : 0;
      if (mappingId > 0) {
        mappingDict = await getMappingDetails(mappingId);
      }

      let requiredCols = templateColumns;
      if (!requiredCols || requiredCols.length === 0) {
        try {
          const payload = buildApiPayload({
            ParentId: parentId ? Number(parentId) : 0
          });
          const resp = await getLeadTemplateJson(payload);
          requiredCols = normalizeTemplateColumns(resp);
        } catch (e) {
          console.error('getLeadTemplateJson error during normalisation', e);
        }
      }

      const normalizedRows = normalizeImportedRows(rows, mappingDict, requiredCols);
      const importJson = JSON.stringify(normalizedRows);
      const response = await addLeadImportSummary(
        buildApiPayload({
          ImportData: importJson,
          UserId: Number(userId || 0),
          ImportTotal: Number(normalizedRows.length || 0),
          filename: String(selectedFile?.name || ''),
          updatematching: updateMatching,
          selectedValue: normalizedSelectedValue
        })
      );

      if (response?.Status !== 1) {
        throw new Error(response?.Message || 'Error Saving Import Data, Please try again or contact support');
      }

      const successMessage = extractImportResultMessage(response?.Details, normalizedRows.length);
      Swal.fire('Success', successMessage, 'success');

      if (typeof onSubmit === 'function') {
        onSubmit({ file: selectedFile, rows: normalizedRows });
      }

      if (typeof onClose === 'function') {
        onClose();
      }

      return true;
    } catch (error) {
      console.error(`${importMode === 'bulk-update' ? 'UpdateMassLeadEnquiry' : 'AddLeadImportSummary'} error:`, error);
      Swal.fire('Error', error?.message || 'Error Processing Data, Please try again or contact support', 'error');
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setIsDownloading(true);

      if (importMode === 'bulk-update') {
        if (selectedFields.length === 0 || selectedFields.length > 5) {
          Swal.fire('Warning', 'Please select atleast one field and max 5', 'warning');
          return;
        }

        const defaultColumns = ['SNo', 'LeadNo', 'CountryCode', 'MobileNo', 'EmailID'];
        const finalColumns = [...new Set([...defaultColumns, ...selectedFields])];
        const emptyRow = finalColumns.map(() => '');
        const worksheet = XLSX.utils.aoa_to_sheet([finalColumns, emptyRow]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'UpdateLeadTemplate');
        XLSX.writeFile(workbook, `UpdateLeadTemplate_${Date.now()}.xlsx`);
        return;
      }

      const columns = importMode === 'bulk-update' && selectedFields.length > 0
        ? selectedFields
        : templateColumns;

      if (!columns.length) {
        await loadLeadTemplateFields();
      }

      const finalColumns = (importMode === 'bulk-update' && selectedFields.length > 0
        ? selectedFields
        : templateColumns).filter(Boolean);

      if (!finalColumns.length) {
        Swal.fire('Warning', 'No template fields available to download.', 'warning');
        return;
      }

      const worksheet = XLSX.utils.aoa_to_sheet([finalColumns]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'LeadImportTemplate');
      XLSX.writeFile(workbook, `LeadImportTemplate_${Date.now()}.xlsx`);
    } catch (error) {
      console.error('Download template error:', error);
      Swal.fire('Error', 'Unable to download template.', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleTemplateSubmit = (templateData) => {
    const templateName = String(templateData?.name || '').trim();
    if (templateName) {
      const nextValue = `custom-${Date.now()}`;
      setTemplates((prev) => [...prev, { value: nextValue, label: templateName }]);
      setSelectedTemplate(nextValue);
    }
    setIsAddTemplateModalOpen(false);
    Swal.fire('Success', 'Template created successfully!', 'success');
  };

  const getSelectedFieldsDisplay = () => {
    if (selectedFields.length === 0) return 'Nothing selected';
    return selectedFields
      .map((value) => bulkUpdateFields.find((f) => f.value === value)?.label)
      .join(', ');
  };

  return (
    <div className="w-full">
      {/* Radio Buttons */}
      <div className="flex items-center gap-6 mb-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="importMode"
            value="bulk-import"
            checked={importMode === 'bulk-import'}
            onChange={(e) => setImportMode(e.target.value)}
            className="w-4 h-4 text-blue-500"
          />
          <span className="text-sm font-medium text-gray-800">Bulk Import</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="importMode"
            value="bulk-update"
            checked={importMode === 'bulk-update'}
            onChange={(e) => setImportMode(e.target.value)}
            className="w-4 h-4 text-blue-500"
          />
          <span className="text-sm font-medium text-gray-800">Bulk Update</span>
        </label>
      </div>

      {/* Template/Field Selection Section */}
      <div className="mb-6">
        {importMode === 'bulk-import' ? (
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Import Template :
            </label>
            <div className="flex-1 flex items-center gap-2">
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {templates.map((template) => (
                  <option key={template.value} value={template.value}>
                    {template.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setIsAddTemplateModalOpen(true)}
                className="p-2 border border-gray-300 rounded hover:bg-gray-50 transition"
                title="Add Template"
              >
                <Plus className="w-4 h-4 text-gray-600" />
              </button>
              <button
                type="button"
                onClick={loadLeadTemplateFields}
                className="p-2 border border-gray-300 rounded hover:bg-gray-50 transition"
                title="Refresh"
                disabled={isLoadingTemplate}
              >
                <RefreshCw className={`w-4 h-4 text-gray-600 ${isLoadingTemplate ? 'animate-spin' : ''}`} />
              </button>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="p-2 border border-gray-300 rounded hover:bg-gray-50 transition"
                title="Download Template"
                disabled={isDownloading}
              >
                <Download className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 relative">
              <div
                onClick={() => setIsFieldDropdownOpen(!isFieldDropdownOpen)}
                className="px-3 py-2 text-sm border border-gray-300 rounded cursor-pointer bg-white hover:border-gray-400 transition text-gray-700"
              >
                {getSelectedFieldsDisplay()}
              </div>

              {isFieldDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-64 overflow-auto">
                  {/* Search Input */}
                  <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                    <input
                      type="text"
                      value={fieldSearchTerm}
                      onChange={(e) => setFieldSearchTerm(e.target.value)}
                      placeholder="Search fields..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {/* Field Options */}
                  <div>
                    {filteredFields.map((field) => (
                      <div
                        key={field.value}
                        onClick={() => handleFieldToggle(field.value)}
                        className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between transition ${selectedFields.includes(field.value)
                          ? 'bg-blue-400 text-white hover:bg-blue-500'
                          : 'text-gray-700 hover:bg-gray-100'
                          }`}
                      >
                        <span>{field.label}</span>
                        {selectedFields.includes(field.value) && (
                          <Check className="w-4 h-4" />
                        )}
                      </div>
                    ))}
                    {filteredFields.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No fields found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="px-4 py-2 text-sm font-medium text-white bg-green-500 rounded hover:bg-green-600 transition whitespace-nowrap"
              disabled={isDownloading || (importMode === 'bulk-update' && isLoadingBulkUpdateFields)}
            >
              {isDownloading ? 'Downloading...' : (isLoadingBulkUpdateFields ? 'Loading Fields...' : 'Download Template')}
            </button>
          </div>
        )}
      </div>

      {/* File Selection */}
      <div className="flex items-center gap-3 mb-6">
        <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
          Select file :
        </label>
        <input
          type="file"
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          className="flex-1 text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
        />
        <button
          type="button"
          onClick={handleUpload}
          disabled={isUploading}
          className="px-6 py-2 text-sm font-medium text-white bg-green-500 rounded hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>


      {/* Instructions */}
      <div className="bg-orange-50 border border-orange-200 rounded p-4">
        <div
          className="flex items-start gap-2 mb-3 cursor-pointer"
          onClick={() => setIsInstructionCollapsed(!isInstructionCollapsed)}
        >
          <div className="w-5 h-5 bg-orange-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-white text-xs font-bold">!</span>
          </div>
          <h3 className="text-sm font-semibold text-orange-800 flex-1">Instruction</h3>
          {isInstructionCollapsed ? (
            <ChevronDown className="w-5 h-5 text-orange-600" />
          ) : (
            <ChevronUp className="w-5 h-5 text-orange-600" />
          )}
        </div>
        {!isInstructionCollapsed && (
          <div className="space-y-2 text-sm text-orange-700">
            {importMode === 'bulk-import' ? (
              <>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>
                    If any discrepancy is found in no. of records uploaded then, please try
                    saving file in proprietary Microsoft Excel Format & try again. If problem
                    persists then please contact Kit19 support.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>Name of the field should be in same sequence as in sample format.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>1st row of excel file will be considered as a header row.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>Keep Sno. Column values sequential</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>
                    Always enter matching CountryCode against entered MobileNo in corresponding
                    CountryCode Column
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>Always enter in each row either MobileNo/EmailID or Both</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>Please keep Date Format as DD-MMM-YYYY e.g. 03-Apr-2006</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>
                    Please ensure that Custom Field Names contains Alphabets[A-Z], Numbers[0-9]
                    or Underscore[_] only. No special characters other than Underscore[_] is
                    allowed.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>Please ensure maximum 10000 records in sheet.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>
                    Please specify which Column (i.e MobileNo/MobileNo1/MobileNo2) contains
                    WhatsApp No for the column WhatsAppNoColName as value.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <a
                    href="#"
                    className="text-blue-600 hover:underline font-medium"
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownloadTemplate();
                    }}
                  >
                    Download Import Template
                  </a>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>
                    If any discrepancy is found in no. of records uploaded, then please try
                    saving file in proprietary Microsoft Excel Format & try again. If problem
                    persists then please contact Kit19 support.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>Name of the field should be in the sequence as follows as in sample</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>1st row of excel file will be considered as a header row.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>Keep Sno. Column values sequential</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>
                    Always enter matching CountryCode against entered MobileNo in corresponding
                    CountryCode Column
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>Always enter in each row either EnquiryID</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>Please keep Date Format as DD-MMM-YYYY e.g. 03-Apr-2005</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>
                    Please ensure that all Products are already entered else those rows will not
                    be Uploaded.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>
                    Please ensure that Custom Field Names contains Alphabets[A-Z], Numbers[0-9]
                    or Underscore[_] only. No special characters other than Underscore[_] is
                    allowed.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">☛</span>
                  <p>Please ensure maximum 100 records in sheet.</p>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add Import Template Modal */}
      <AddImportTemplate
        isOpen={isAddTemplateModalOpen}
        onClose={() => setIsAddTemplateModalOpen(false)}
        onSubmit={handleTemplateSubmit}
      />
    </div>
  );
};

export default ImportData;
